import { combine, createStore, createEvent, createEffect, sample } from 'effector';
import { axiosClient } from '@nx-frontend/shared/api';
import { generalEnv } from '@nx-frontend/shared/env';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $isAppSumoUser, $user } from '@nx-frontend/shared/store/user';
import { $isSubscriptionSelected, $selectedSubscription, $userVars } from '@nx-frontend/shared/store/user-vars';
import { BillingInfoPlan, OrgInfoSubscriptionType, TrialServiceBusinessTrialDto } from '@nx-frontend/shared/types';

export const initialTrialsState: TrialServiceBusinessTrialDto[] = [];

export const $trials = createStore(initialTrialsState);

export const fetchTrialsEvent = createEvent();

export const fetchTrialsFx = createEffect(async () => {
  const { data } = await axiosClient.get<TrialServiceBusinessTrialDto[]>(`${generalEnv.nextApiPath}/orgs/trials`);
  return data;
});

export const $isTrialsLoaded = createStore(false).on(fetchTrialsFx.done, () => true);

sample({
  source: $trials,
  clock: fetchTrialsEvent,
  fn: (_, params) => params,
  target: [fetchTrialsFx],
});

$trials.on(fetchTrialsFx.doneData, (_, trials) => {
  return trials;
});

export const $isSomeBusinessTrialExpired = $trials.map((trials) => {
  if (!trials) {
    return false;
  }

  if (!trials.length) {
    return false;
  }

  return !!trials.find(({ state }) => state === 'expired');
});

export const $isTrialWithCardUsed = $trials.map((trials) => {
  return !!(trials && trials.length);
});

export const $businessTrial = combine($trials, $currentOrganization, (trials, currentOrganization) => {
  if (!trials) {
    return null;
  }

  if (!trials.length) {
    return null;
  }

  if (!currentOrganization) {
    return null;
  }

  return trials.find((trial) => trial.orgId === currentOrganization.globalId);
});

export const $isBusinessTrialActive = combine($businessTrial, (trial) => {
  if (!trial) {
    return false;
  }

  return trial && trial.state === 'active';
});

export const $isBusinessTrialPastDue = combine($businessTrial, (trial) => {
  if (!trial) {
    return false;
  }

  return trial && trial.state === 'pastdue';
});

export const $showActivateTrial = combine(
  $user,
  $billingInfo,
  $userVars,
  $orgInfo,
  $currentOrganization,
  $isSubscriptionSelected,
  $selectedSubscription,
  $isSomeBusinessTrialExpired,
  $isTrialWithCardUsed,
  $isBusinessTrialActive,
  $isAppSumoUser,
  (
    user,
    billingInfo,
    userVars,
    orgInfo,
    currentOrganization,
    isSubscriptionSelected,
    selectedSubscription,
    isSomeBusinessTrialExpired,
    isTrialWithCardUsed,
    isBusinessTrialActive,
    isAppSumoUser
  ) => {
    const currentUserId = user ? user.id : 0;
    if (!user || !billingInfo || !currentOrganization || isAppSumoUser) {
      return false;
    }

    if (!user.email) {
      return false;
    }

    const { createdAt, userId } = currentOrganization;
    if (!userId) {
      return false;
    }
    const dateLimit = 1677628800; // 2023-03-01 00:00:00

    if (createdAt < dateLimit) {
      return false;
    }

    if (currentUserId && userId !== currentUserId) {
      return false;
    }

    if (billingInfo.plan === BillingInfoPlan.TRIAL) {
      return false;
    }

    if (isBusinessTrialActive || isSomeBusinessTrialExpired || isTrialWithCardUsed) {
      return false;
    }

    if (isSubscriptionSelected && !selectedSubscription) {
      return false;
    }

    return (
      !isSubscriptionSelected ||
      (isSubscriptionSelected &&
        !isTrialWithCardUsed &&
        ![OrgInfoSubscriptionType.FREE_ACCOUNT, OrgInfoSubscriptionType.STANDARD].includes(
          selectedSubscription as OrgInfoSubscriptionType
        ))
    );
  }
);

export const $showPastDueTrial = combine(
  $user,
  $currentOrganization,
  $isAppSumoUser,
  $isBusinessTrialPastDue,
  (user, currentOrganization, isAppSumoUser, isBusinessTrialPastDue) => {
    const currentUserId = user ? user.id : 0;
    if (!user || !currentOrganization || isAppSumoUser) {
      return false;
    }

    if (!user.email) {
      return false;
    }

    const { userId } = currentOrganization;
    if (!userId) {
      return false;
    }

    if (currentUserId && userId !== currentUserId) {
      return false;
    }

    return isBusinessTrialPastDue;
  }
);
